import Vue from "vue";
import Vuex from "vuex";
import { websocket } from "@/config";
import { AppWebSocket } from "@/helpers/websocket";
import { AppStore } from "@/types/store";
import { websocketPlugin } from "@/plugins/websocket";

import AuthModule from "./auth.store";
import EntriesModule from "./entries.store";
import SettingsModule from "./settings.store";
import InvoiceModule from "./invoice.store";
import ClientsModule from "./clients.store";
import UsersModule from "./users.store";
import DashboardModule from "./dashboard.store";
import VendorsModule from "./vendors.store";
import ReportsModule from "./reports.store";
import CostsModule from "./costs.store";

import { MessageBody } from "@/types/websocket";
import {
  isConnectionError,
  isGeneralError,
  isServerError,
} from "@/helpers/errorChecker";

Vue.use(Vuex);

export const store = new Vuex.Store<AppStore>({
  state: {
    loading: false,
    ws: null,
    connected: false,
    reconnecting: false,
    message: "",
    _notifications: [],
    disableSelectClient: false,
  },

  getters: {
    notifications: (state) => state._notifications,
    loading: (state) => state.loading,
  },

  mutations: {
    SET_WS: (state, payload: AppWebSocket) => (state.ws = payload),
    CONNECTION: (state, payload) => {
      if (payload?.connected) {
        state.connected = true;
      } else {
        state.connected = false;
      }
    },
    RECONNECTION: (state, payload) => (state.reconnecting = payload),
    NOTIFICATION: (state, payload) => state._notifications.push(payload),
    SET_LOADING: (state, payload) => (state.loading = payload),
  },

  actions: {
    setWebSocket: ({ commit }) => {
      try {
        const ws = new AppWebSocket(
          `${websocket}?token=${store.getters["auth/token"]}`,
          store.getters["auth/token"],
        );
        commit("SET_WS", ws);
      } catch (e) {
        console.log(e);
        window.location.reload;
      }
    },
    emit: ({ state, commit }, payload: MessageBody) => {
      commit("SET_LOADING", true);
      try {
        state.ws?.emit(payload);
      } catch (e) {
        state.connected = false;
        commit("SET_WS", null);
      }
    },
    asyncEmit: async ({ state, commit }, payload: MessageBody) => {
      commit("SET_LOADING", true);
      try {
        await state.ws?.asyncEmit(payload);
      } catch (err) {
        const notification = {
          title: "Error occured during saving the project",
          notification:
            "Your changes were not saved. Please try again by clicking SAVE button. Refreshing the page may cause your changes to disappear.",
        };
        if (err instanceof Error) {
          if (err.cause && err.cause == "CLIENT_CONNECTION") {
            console.log("Lost connection client side error:", err);
            state.connected = false;
            commit("SET_WS", null);
            commit("SET_LOADING", false);
            throw new Error("Lost connection");
          } else {
            console.log(err)
            console.error("Something went wrong");
            store.dispatch("pushErrorNotification", notification);
            throw new Error("Something went wrong");
          }
        } else {
          if (isConnectionError(err)) {
            console.log("Lost connection network issue error:", err);
            state.connected = false;
            commit("SET_WS", null);
            commit("SET_LOADING", false);
            throw new Error("Lost connection");
          }

          if (isGeneralError(err) || isServerError(err)) {
            console.error("Something went wrong");
            store.dispatch("pushErrorNotification", notification);
            throw new Error("Something went wrong");
          }
        }
      }
    },
    reconnect: async ({ state, commit }) => {
      try {
        if (!navigator.onLine) return;
        commit("RECONNECTION", true);
        await store.dispatch("auth/currentUser");
        commit("SET_LOADING", false);
      } catch (e) {
        console.log(e);
        state.connected = false;
        commit("RECONNECTION", false);
        commit("SET_LOADING", false);
      }
    },
    pushNotification: ({ commit }, notification) => {
      Vue.prototype.$notify({
        group: "n",
        title: "Important message",
        type: "info",
        text: notification,
      });
      commit("NOTIFICATION", notification);
    },
    pushErrorNotification: ({ commit }, { title, notification }) => {
      Vue.prototype.$notify({
        group: "n",
        title: title,
        type: "error",
        duration: 5000,
        text: notification,
      });
      commit("NOTIFICATION", notification);
    },

    showSnackbar: ({ state }, message) => {
      state.message = message;
    },
  },

  modules: {
    auth: AuthModule,
    entries: EntriesModule,
    settings: SettingsModule,
    invoice: InvoiceModule,
    clients: ClientsModule,
    users: UsersModule,
    dashboard: DashboardModule,
    vendors: VendorsModule,
    reports: ReportsModule,
    costs: CostsModule,
  },
  plugins: [websocketPlugin()],
});
